import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CreditCard, History, RefreshCw, ShieldCheck, User } from 'lucide-react'
import { formatDateTime } from '@/lib/datetime'
import { formatMinorCurrency, humanizeEnum } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import type { AdminRefund } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { KeyValue, KeyValueGrid } from '@/components/ui/RowActions'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { fetchRefundHistory, reconcileRefund } from './api'
import styles from './RefundDetailsDrawer.module.css'

interface RefundDetailsDrawerProps {
  refund: AdminRefund | null
  onClose: () => void
}

/**
 * Everything recorded about one refund attempt.
 *
 * An operator opens this to answer one of two questions: "did the customer get
 * their money?" and, when the answer is unclear, "what do I do about it?". The
 * guidance panel exists for the second — an unsettled refund with no next step
 * is how a customer ends up refunded twice.
 */
export function RefundDetailsDrawer({ refund, onClose }: RefundDetailsDrawerProps) {
  const open = refund !== null

  const historyQuery = useQuery({
    queryKey: queryKeys.refunds.history(refund?.id ?? 0),
    queryFn: () => fetchRefundHistory(refund?.id as number),
    enabled: open,
  })

  const reconcileMutation = useAdminMutation({
    mutationFn: (id: number) => reconcileRefund(id),
    successTitle: 'Refund re-checked with Paymob',
    successDescription: (updated) =>
      updated.status === 'SUCCEEDED'
        ? 'Paymob confirmed the refund. It is now settled.'
        : updated.status === 'FAILED'
          ? 'Paymob has no matching refund. No money moved.'
          : 'Paymob still reports this refund as unsettled.',
    invalidate: [queryKeys.refunds.all, queryKeys.tripRequests.all, queryKeys.dashboard.all],
    errorTitle: 'Could not re-check this refund',
  })

  const settledAmount = refund?.refundedAmountMinor ?? refund?.requestedAmountMinor ?? 0
  const history = historyQuery.data ?? []
  const related = history.filter((entry) => entry.id !== refund?.id)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      variant="drawer"
      size="lg"
      busy={reconcileMutation.isPending}
      title={refund ? `Refund #${refund.id}` : 'Refund'}
      description="The permanent record of this refund attempt, exactly as stored."
      footer={
        <Button variant="ghost" onClick={onClose} disabled={reconcileMutation.isPending}>
          Close
        </Button>
      }
    >
      {refund ? (
        <div className={styles.body}>
          <section className={styles.statusRow}>
            <StatusBadge status={refund.status} />
            {refund.source ? <span className={styles.chip}>{humanizeEnum(refund.source)}</span> : null}
            <span className={styles.amount}>{formatMinorCurrency(settledAmount, refund.currency)}</span>
          </section>

          {refund.needsAttention ? (
            <section className={styles.attention} role="note">
              <AlertTriangle aria-hidden="true" />
              <div>
                <p className={styles.attentionTitle}>
                  {refund.status === 'REQUIRES_RECONCILIATION'
                    ? 'This refund’s outcome is unknown'
                    : 'Paymob has not settled this refund yet'}
                </p>
                <p>
                  {refund.status === 'REQUIRES_RECONCILIATION'
                    ? 'Paymob did not answer, so the money may or may not have moved. Do not issue another refund — re-check with Paymob first. It is the only safe way to find out.'
                    : 'Paymob accepted the refund and is still processing it. It settles automatically when Paymob calls back; re-check if it has been unusually long.'}
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={<RefreshCw aria-hidden="true" />}
                  loading={reconcileMutation.isPending}
                  onClick={() => reconcileMutation.mutate(refund.id)}
                >
                  Re-check with Paymob
                </Button>
              </div>
            </section>
          ) : null}

          {/* Money -------------------------------------------------------- */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <CreditCard aria-hidden="true" />
              Amounts
            </h3>
            <KeyValueGrid>
              <KeyValue label="Original payment">
                {formatMinorCurrency(refund.originalAmountMinor, refund.currency)}
              </KeyValue>
              <KeyValue label="Requested">
                {formatMinorCurrency(refund.requestedAmountMinor, refund.currency)}
              </KeyValue>
              <KeyValue label="Confirmed refunded">
                {refund.refundedAmountMinor !== null ? (
                  formatMinorCurrency(refund.refundedAmountMinor, refund.currency)
                ) : (
                  <span className={styles.muted}>Nothing confirmed</span>
                )}
              </KeyValue>
              <KeyValue label="Currency">{refund.currency}</KeyValue>
            </KeyValueGrid>
          </section>

          {/* Customer and booking ---------------------------------------- */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <User aria-hidden="true" />
              Customer &amp; booking
            </h3>
            <KeyValueGrid>
              <KeyValue label="Customer">{refund.customerName ?? '—'}</KeyValue>
              <KeyValue label="Customer ID">{refund.customerUserId ?? '—'}</KeyValue>
              <KeyValue label="Booking reference">{refund.bookingReference ?? '—'}</KeyValue>
              <KeyValue label="Trip request">{refund.tripRequestId ?? '—'}</KeyValue>
              <KeyValue label="Trip">{refund.tripId ?? '—'}</KeyValue>
              <KeyValue label="Payment method">{refund.paymentMethod ?? '—'}</KeyValue>
            </KeyValueGrid>
          </section>

          {/* Why and who -------------------------------------------------- */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <ShieldCheck aria-hidden="true" />
              Authorisation
            </h3>
            <KeyValueGrid>
              <KeyValue label="Reason" wide>
                {refund.reason}
              </KeyValue>
              {refund.adminNote ? (
                <KeyValue label="Internal note" wide>
                  {refund.adminNote}
                </KeyValue>
              ) : null}
              <KeyValue label="Requested by">
                {refund.adminName ?? (refund.source === 'MANUAL_ADMIN' ? '—' : 'System')}
                {refund.adminEmail ? <div className={styles.muted}>{refund.adminEmail}</div> : null}
              </KeyValue>
              <KeyValue label="Source">{humanizeEnum(refund.source)}</KeyValue>
              <KeyValue label="Idempotency key" wide>
                <span className={styles.mono}>{refund.idempotencyKey}</span>
              </KeyValue>
            </KeyValueGrid>
          </section>

          {/* Gateway ------------------------------------------------------ */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Paymob</h3>
            <KeyValueGrid>
              <KeyValue label="Original transaction">
                <span className={styles.mono}>{refund.paymobTransactionId ?? '—'}</span>
              </KeyValue>
              <KeyValue label="Refund transaction">
                <span className={styles.mono}>{refund.paymobRefundTransactionId ?? 'Not issued'}</span>
              </KeyValue>
              <KeyValue label="Order">
                <span className={styles.mono}>{refund.paymobOrderId ?? '—'}</span>
              </KeyValue>
              <KeyValue label="Response code">{refund.gatewayResponseCode ?? '—'}</KeyValue>
              <KeyValue label="HTTP status">{refund.gatewayHttpStatus ?? '—'}</KeyValue>
              <KeyValue label="Attempts">{refund.attemptCount}</KeyValue>
            </KeyValueGrid>

            {refund.gatewayMessage ? <p className={styles.gatewayMessage}>{refund.gatewayMessage}</p> : null}

            {refund.gatewayMetadata ? (
              <details className={styles.metadata}>
                <summary>Gateway response detail</summary>
                <pre>{prettyJson(refund.gatewayMetadata)}</pre>
                <p className={styles.metadataNote}>
                  A sanitised subset of Paymob&rsquo;s response. Card numbers, tokens and credentials are never
                  stored.
                </p>
              </details>
            ) : null}
          </section>

          {/* Timeline ----------------------------------------------------- */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <History aria-hidden="true" />
              Timeline
            </h3>
            <ol className={styles.timeline} role="list">
              <TimelineStep label="Requested" at={refund.requestedAt ?? refund.createdAt} done />
              <TimelineStep label="Sent to Paymob" at={refund.createdAt} done />
              <TimelineStep
                label={refund.status === 'FAILED' ? 'Rejected by Paymob' : 'Confirmed by Paymob'}
                at={refund.completedAt}
                done={refund.completedAt !== null}
                pendingLabel="Not settled yet"
              />
              {refund.lastReconciledAt ? (
                <TimelineStep label="Last re-checked" at={refund.lastReconciledAt} done />
              ) : null}
            </ol>
          </section>

          {/* Sibling attempts --------------------------------------------- */}
          {related.length > 0 ? (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Other attempts on this payment</h3>
              <ul className={styles.related} role="list">
                {related.map((entry) => (
                  <li key={entry.id} className={styles.relatedItem}>
                    <StatusBadge status={entry.status} />
                    <span className={styles.relatedAmount}>
                      {formatMinorCurrency(entry.refundedAmountMinor ?? entry.requestedAmountMinor, entry.currency)}
                    </span>
                    <span className={styles.muted}>{formatDateTime(entry.createdAt)}</span>
                    <span className={styles.relatedSource}>{humanizeEnum(entry.source)}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  )
}

function TimelineStep({
  label,
  at,
  done,
  pendingLabel = 'Pending',
}: {
  label: string
  at: string | null
  done: boolean
  pendingLabel?: string
}) {
  return (
    <li className={done ? styles.timelineDone : styles.timelinePending}>
      <span className={styles.timelineLabel}>{label}</span>
      <span className={styles.timelineValue}>{at ? formatDateTime(at) : pendingLabel}</span>
    </li>
  )
}

/** The metadata is stored compact; reading it in a drawer wants line breaks. */
function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}
